import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, Image, Pressable, StyleSheet, TextInput, ScrollView } from 'react-native-web';
import Modal from 'react-modal';
import { createBusinessImage, updateBusinessInfo } from '../services/BusinessService';
import Button from './Button';
import IconButton from './IconButton';
import pencilIcon from '../icons/blue-pencil.png';
import instagramIcon from '../icons/DupeIcons-Instagram.png';
import tiktokIcon from '../icons/DupeIcons-TikTOk.png';

// Custom hook for business editing logic
const useBusinessEdit = (business, onUpdate) => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const openModal = useCallback(() => {
    setFormData({ ...business });
    setEditModalVisible(true);
    setErrors({});
  }, [business]);

  const closeModal = useCallback(() => {
    setEditModalVisible(false);
    setFormData({});
    setErrors({});
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const setUploadImage = useCallback((file) => {
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, logo: 'Please select a valid image file (JPEG, PNG, or WebP)' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setErrors(prev => ({ ...prev, logo: 'Image size must be less than 5MB' }));
        return;
      }
      setFormData(prev => ({ ...prev, logo: file, newLogo: true }));
      setErrors(prev => ({ ...prev, logo: null }));
    }
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = 'Business name is required';
    }
    if (formData.name?.length > 50) {
      newErrors.name = 'Business name must be less than 50 characters';
    }
    if (formData.description?.length > 115) {
      newErrors.description = 'Description must be less than 115 characters';
    }
    return newErrors;
  }, [formData]);

  const saveBusinessInfo = useCallback(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    try {
      let logoImgId = formData.logoImgId;

      if (formData.newLogo && formData.logo) {
        const formDataObj = new FormData();
        formDataObj.append('img', formData.logo);
        const imageResp = await createBusinessImage(formDataObj);
        if (imageResp.status === 200 && imageResp.data?.id) {
          logoImgId = imageResp.data.id;
        }
      }

      const updateData = {
        name: formData.name?.trim(),
        description: formData.description?.trim(),
        logoImgId,
        instagramHandle: formData.instagramHandle?.trim(),
        tikTokHandle: formData.tikTokHandle?.trim(),
        youtubeHandle: formData.youtubeHandle?.trim(),
      };

      const response = await updateBusinessInfo(updateData);
      if (response.status === 200 && response.data) {
        onUpdate(response.data);
        closeModal();
      }
    } catch (error) {
      console.error('Error updating business info:', error);
      setErrors({ general: 'Failed to update business information. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  }, [formData, validateForm, onUpdate, closeModal]);

  return {
    editModalVisible,
    formData,
    isLoading,
    errors,
    openModal,
    closeModal,
    updateField,
    setUploadImage,
    saveBusinessInfo,
  };
};

const BusinessInfoCard = React.memo(({ business, onBusinessUpdate }) => {
  const {
    editModalVisible,
    formData,
    isLoading,
    errors,
    openModal,
    closeModal,
    updateField,
    setUploadImage,
    saveBusinessInfo,
  } = useBusinessEdit(business, onBusinessUpdate);

  // Memoized logo source calculation
  const logoSource = useMemo(() => {
    if (formData.newLogo && formData.logo) {
      return { uri: URL.createObjectURL(formData.logo) };
    }
    if (business?.logoExists) {
      return { uri: `https://d3p3fw3rutb1if.cloudfront.net/business/photos/${business.businessId}` };
    }
    return { uri: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxjb2xsZWN0aW9uLXBhZ2V8MXw3NjA4Mjc3NHx8ZW58MHx8fHw%3D&w=1000&q=80' };
  }, [business?.logoExists, business?.businessId, formData.newLogo, formData.logo]);

  const editModalLogoSource = useMemo(() => {
    if (formData.newLogo && formData.logo) {
      return { uri: URL.createObjectURL(formData.logo) };
    }
    if (formData.logoExists) {
      return { uri: `https://d3p3fw3rutb1if.cloudfront.net/business/photos/${formData.businessId}` };
    }
    return { uri: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxjb2xsZWN0aW9uLXBhZ2V8MXw3NjA4Mjc3NHx8ZW58MHx8fHw%3D&w=1000&q=80' };
  }, [formData.newLogo, formData.logo, formData.logoExists, formData.businessId]);

  // Memoized social media links
  const socialLinks = useMemo(() => {
    const links = [];
    if (business?.instagramHandle) {
      links.push({
        platform: 'instagram',
        url: `https://www.instagram.com/${business.instagramHandle}`,
        icon: instagramIcon,
      });
    }
    if (business?.tikTokHandle) {
      links.push({
        platform: 'tiktok',
        url: `https://www.tiktok.com/@${business.tikTokHandle}`,
        icon: tiktokIcon,
      });
    }
    return links;
  }, [business?.instagramHandle, business?.tikTokHandle]);

  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        setUploadImage(file);
      }
    };
    input.click();
  }, [setUploadImage]);

  if (!business) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading business information...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <View style={styles.logoContainer}>
          <Image 
            source={logoSource}
            style={styles.logoImage}
            accessible={true}
            accessibilityLabel={`${business.name} logo`}
          />
          <View style={styles.socialLinksContainer}>
            {socialLinks.map((link) => (
              <Pressable
                key={link.platform}
                onPress={() => window.open(link.url, '_blank')}
                style={({ hovered }) => [
                  styles.socialLink,
                  hovered && styles.socialLinkHovered
                ]}
                accessible={true}
                accessibilityLabel={`Visit ${business.name} on ${link.platform}`}
                accessibilityRole="link"
              >
                <Image source={{ uri: link.icon }} style={styles.socialIcon} />
              </Pressable>
            ))}
          </View>
        </View>
        
        <View style={styles.businessInfoContainer}>
          <View style={styles.businessNameRow}>
            <Text style={styles.businessName} numberOfLines={2}>
              {business.name}
            </Text>
            <IconButton
              source={{ uri: pencilIcon }}
              onPress={openModal}
              size={18}
              accessibilityLabel="Edit business information"
              containerStyle={styles.editButton}
            />
          </View>
          <Text style={styles.businessDescription} numberOfLines={4}>
            {business.description}
          </Text>
        </View>
      </View>

      <Modal
        isOpen={editModalVisible}
        onRequestClose={closeModal}
        style={styles.modalStyles}
        ariaHideApp={false}
        contentLabel="Edit Business Information"
      >
        <View style={styles.modalContainer}>
          <ScrollView showsVerticalScrollIndicator={false} style={styles.scrollView}>
            <Text style={styles.modalTitle}>Edit Business Info</Text>
            
            {errors.general && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>{errors.general}</Text>
              </View>
            )}

            <View style={styles.formContainer}>
              <View style={styles.logoSection}>
                <Pressable
                  onPress={handleImageUpload}
                  style={({ hovered }) => [
                    styles.logoUploadContainer,
                    hovered && styles.logoUploadHovered
                  ]}
                  accessible={true}
                  accessibilityLabel="Upload business logo"
                  accessibilityRole="button"
                >
                  <Image 
                    source={editModalLogoSource}
                    style={styles.logoPreview}
                  />
                  <View style={styles.logoOverlay}>
                    <Text style={styles.logoOverlayText}>Change Logo</Text>
                  </View>
                </Pressable>
                {errors.logo && (
                  <Text style={styles.fieldErrorText}>{errors.logo}</Text>
                )}
              </View>

              <View style={styles.formFields}>
                <View style={styles.fieldContainer}>
                  <TextInput
                    value={formData.name || ''}
                    onChangeText={(text) => updateField('name', text)}
                    placeholder="Enter your business name"
                    placeholderTextColor="#9ca3af"
                    style={[styles.textInput, errors.name && styles.textInputError]}
                    maxLength={50}
                    accessible={true}
                    accessibilityLabel="Business name"
                  />
                  {errors.name && (
                    <Text style={styles.fieldErrorText}>{errors.name}</Text>
                  )}
                </View>

                <View style={styles.fieldContainer}>
                  <View style={styles.textareaContainer}>
                    <TextInput
                      value={formData.description || ''}
                      onChangeText={(text) => updateField('description', text)}
                      placeholder="Tell customers about your business..."
                      placeholderTextColor="#9ca3af"
                      style={[styles.textArea, errors.description && styles.textInputError]}
                      multiline
                      numberOfLines={4}
                      maxLength={115}
                      accessible={true}
                      accessibilityLabel="Business description"
                    />
                    <Text style={styles.characterCount}>
                      {115 - (formData.description?.length || 0)}
                    </Text>
                  </View>
                  {errors.description && (
                    <Text style={styles.fieldErrorText}>{errors.description}</Text>
                  )}
                </View>

                <View style={styles.socialHandlesContainer}>
                  <View style={styles.socialHandleField}>
                    <Text style={styles.atSymbol}>@</Text>
                    <TextInput
                      value={formData.instagramHandle || ''}
                      onChangeText={(text) => updateField('instagramHandle', text)}
                      placeholder="your_instagram_handle"
                      placeholderTextColor="#9ca3af"
                      style={[
                        styles.socialInput,
                        !formData.instagramHandle && styles.socialInputEmpty
                      ]}
                      accessible={true}
                      accessibilityLabel="Instagram handle"
                    />
                  </View>
                  
                  <View style={styles.socialHandleField}>
                    <Text style={styles.atSymbol}>@</Text>
                    <TextInput
                      value={formData.tikTokHandle || ''}
                      onChangeText={(text) => updateField('tikTokHandle', text)}
                      placeholder="your_tiktok_handle"
                      placeholderTextColor="#9ca3af"
                      style={[
                        styles.socialInput,
                        !formData.tikTokHandle && styles.socialInputEmpty
                      ]}
                      accessible={true}
                      accessibilityLabel="TikTok handle"
                    />
                  </View>
                  
                  <View style={styles.socialHandleField}>
                    <Text style={styles.atSymbol}>@</Text>
                    <TextInput
                      value={formData.youtubeHandle || ''}
                      onChangeText={(text) => updateField('youtubeHandle', text)}
                      placeholder="your_youtube_handle"
                      placeholderTextColor="#9ca3af"
                      style={[
                        styles.socialInput,
                        !formData.youtubeHandle && styles.socialInputEmpty
                      ]}
                      accessible={true}
                      accessibilityLabel="YouTube handle"
                    />
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <Button 
                text="Cancel" 
                onPress={closeModal} 
                variant="secondary" 
                style={styles.cancelButton}
                disabled={isLoading}
              />
              <Button 
                text={isLoading ? "Saving..." : "Save Changes"} 
                onPress={saveBusinessInfo}
                style={styles.saveButton}
                disabled={isLoading}
              />
            </View>
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
    position: 'relative',
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#737373',
    fontFamily: "'lato', sans-serif",
  },
  contentContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  logoContainer: {
    marginRight: 20,
    alignItems: 'center',
  },
  logoImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginBottom: 10,
  },
  socialLinksContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  socialLink: {
    padding: 4,
    borderRadius: 6,
    transition: 'transform 0.2s ease',
  },
  socialLinkHovered: {
    transform: [{ scale: 1.1 }],
  },
  socialIcon: {
    width: 20,
    height: 20,
  },
  businessInfoContainer: {
    flex: 1,
    paddingTop: 5,
  },
  businessNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  businessName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#396de0',
    fontFamily: "'forma-djr-display', sans-serif",
    flex: 1,
    marginRight: 10,
  },
  businessDescription: {
    fontSize: 16,
    color: '#737373',
    lineHeight: 22,
    fontFamily: "'lato', sans-serif",
  },
  editButton: {
    // IconButton container style - minimal styling since IconButton handles padding/borders
  },
  modalStyles: {
    overlay: {
      backgroundColor: 'rgba(57, 109, 224, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      position: 'relative',
      maxWidth: 600,
      width: '90%',
      maxHeight: '90%',
      border: 'none',
      borderRadius: 15,
      padding: 0,
      background: 'white',
    },
  },
  modalContainer: {
    flex: 1,
  },
  scrollView: {
    padding: 30,
  },
  modalTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#396de0',
    fontFamily: "'forma-djr-display', sans-serif",
    textAlign: 'center',
    marginBottom: 20,
  },
  errorContainer: {
    backgroundColor: '#fef2f2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: '#ef4444',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
    fontFamily: "'lato', sans-serif",
  },
  formContainer: {
    marginBottom: 30,
  },
  logoSection: {
    alignItems: 'center',
    marginBottom: 25,
  },
  logoUploadContainer: {
    position: 'relative',
    borderRadius: 60,
    overflow: 'hidden',
  },
  logoUploadHovered: {
    opacity: 0.8,
  },
  logoPreview: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  logoOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingVertical: 8,
    alignItems: 'center',
  },
  logoOverlayText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
    fontFamily: "'lato', sans-serif",
  },
  formFields: {
    gap: 20,
  },
  fieldContainer: {
    width: '100%',
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    fontFamily: "'lato', sans-serif",
    transition: 'border-color 0.2s ease',
    outlineStyle: 'none',
    color: '#374151',
    fontWeight: '500',
  },
  textInputError: {
    borderColor: '#ef4444',
  },
  textareaContainer: {
    position: 'relative',
  },
  textArea: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    fontFamily: "'lato', sans-serif",
    minHeight: 100,
    textAlignVertical: 'top',
    paddingRight: 50,
    outlineStyle: 'none',
    color: '#374151',
    fontWeight: '500',
  },
  characterCount: {
    position: 'absolute',
    bottom: 8,
    right: 12,
    fontSize: 12,
    color: '#9ca3af',
    fontFamily: "'lato', sans-serif",
  },
  fieldErrorText: {
    color: '#dc2626',
    fontSize: 12,
    marginTop: 4,
    fontFamily: "'lato', sans-serif",
  },
  socialHandlesContainer: {
    gap: 15,
  },
  socialHandleField: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingLeft: 12,
  },
  atSymbol: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6b7280',
    marginRight: 8,
    fontFamily: "'lato', sans-serif",
  },
  socialInput: {
    flex: 1,
    padding: 12,
    fontSize: 16,
    fontFamily: "'lato', sans-serif",
    outlineStyle: 'none',
    borderWidth: 0,
    color: '#374151',
    fontWeight: '500',
  },
  socialInputEmpty: {
    fontStyle: 'italic',
    fontWeight: '400',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
    marginTop: 20,
  },
  cancelButton: {
    minWidth: 120,
  },
  saveButton: {
    minWidth: 120,
  },
});

BusinessInfoCard.displayName = 'BusinessInfoCard';

export default BusinessInfoCard; 